import { InlineAlertType } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, ScrollView, StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import {
  otpSchema,
  otpValidationMessages,
} from 'shared/components/vxl/input/schemas/otp.schema';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import KeyboardAvoidingContainer from 'shared/containers/KeyboardAvoidingContainer/KeyboardAvoidingContainer';
import theme from 'shared/styles/theme';
import { formatOtp, OTP_CODE_LENGTH } from 'shared/utils/string/otp';

import { OtpFlowKey } from './constants/OtpFlowKey';
import { OtpVerificationType } from './constants/OtpVerificationType';
import {
  AnalyticsErrorState,
  useOtpAnalytics,
} from './hooks/useOtpAnalytics/useOtpAnalytics';
import {
  InformationScreenTypeEnum,
  inlineErrorCodes,
  screenLevelErrorMessageCodes,
} from './information/InformationScreenTypeEnum';
import ResendOtpButton from './ResendOtpButton/ResendOtpButton';

export interface OtpScreenProps<TVerifyResult = unknown> {
  flowKey: OtpFlowKey;
  verificationType: OtpVerificationType;

  // Text content
  headerText?: string;
  submitButtonLabel?: string;
  secondaryButtonLabel?: string;
  invalidCodeText?: string;
  genericErrorText?: string;
  newCodeSentText?: string;

  // Interpolated items
  description?: React.ReactNode;

  // External state
  isLoading: boolean;
  // AutoFocus state
  shouldAutoFocus?: boolean;

  // Configurable error codes
  screenLevelErrorCodes?: (number | string)[];

  // Event handlers
  onVerifyOtp: (code: string) => Promise<TVerifyResult>;
  onResendOtp: () => Promise<{
    errorCode?: number | string;
    title?: string;
    message?: string;
  }>;
  onBack: () => void;
  onPostSuccess?: (result?: TVerifyResult) => void;
  secondaryButtonOnPress?: () => void;
  secondaryButtonAnalyticsId?: string;
  isCode?: boolean;
  screenTitle?: string;
  onCodeExpired?: () => void;
  onScreenLevelError?: (error: {
    errorCode: string;
    title?: string;
    message?: string;
  }) => void;
}

// Make OtpScreen a generic component
function OtpScreen<
  TVerifyResult extends {
    success?: boolean;
    errorCode?: number | string;
    title?: string;
    message?: string;
    data?: unknown;
  },
>({
  flowKey,
  verificationType,

  // Text content
  headerText,
  submitButtonLabel,
  secondaryButtonLabel,
  invalidCodeText,
  genericErrorText,
  newCodeSentText,

  // Interpolated items
  description,

  // External state
  isLoading,
  // AutoFocus state
  shouldAutoFocus,

  // Event handlers
  onVerifyOtp,
  onResendOtp,
  onBack,
  onPostSuccess,
  secondaryButtonOnPress,
  secondaryButtonAnalyticsId,
  isCode = false,
  screenTitle: screenTitle,
  onCodeExpired,
  onScreenLevelError,
}: OtpScreenProps<TVerifyResult>) {
  const router = useRouter();
  const [otp, setOtp] = useState<string>('');
  const [otpError, setOtpError] = useState<string | null>();
  const [screenError, setScreenError] = useState<{
    title?: string;
    message?: string;
  } | null>(null);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [otpSubmitAttempt, setOtpSubmitAttempt] = useState(1);
  const [resendCodeAttempt, setResendCodeAttempt] = useState(1);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [analyticsError, setAnalyticsError] =
    useState<AnalyticsErrorState | null>(null);

  const { trackVerificationAttempt, trackVerificationFailed } = useOtpAnalytics(
    {
      flowKey,
      verificationType,
      otpSubmitAttempt,
      isSuccess: verificationSuccess,
      analyticsError,
    },
  );

  const { t: translatedTextOtp } = useTranslation('otp');
  const invalidCode = invalidCodeText ?? otpValidationMessages.invalid;
  const genericError = genericErrorText ?? translatedTextOtp('genericError');
  const { t: translatedTextOtpError } = useTranslation('otp', {
    keyPrefix: 'otpError',
  });

  // Internal handlers
  const handleOtpChange = (text: string) => {
    const numericText = formatOtp(text);

    setVerificationSuccess(false);
    setOtp(numericText);
  };

  const handleVerifyOtp = async () => {
    setAnalyticsError(null);
    setVerificationSuccess(false);

    if (!otpSchema.safeParse(otp).success) {
      setOtpError(invalidCode);
      return;
    }

    // Log the appropriate analytics events
    trackVerificationAttempt();
    setOtpSubmitAttempt(otpSubmitAttempt + 1);

    try {
      const result = await onVerifyOtp(otp || '');

      if (result?.success) {
        setVerificationSuccess(true);
        onPostSuccess?.(result);
        setScreenError(null);
        setOtpError(null);
      } else {
        setAnalyticsError({
          reason: result?.message ?? translatedTextOtpError('tryAgainMessage'),
          code: result?.errorCode ?? invalidCode,
          message: result?.message ?? translatedTextOtpError('tryAgainMessage'),
          title: result?.title ?? invalidCode,
        });
        if (result.errorCode) {
          if (
            screenLevelErrorMessageCodes.includes(result.errorCode.toString())
          ) {
            if (onScreenLevelError) {
              onScreenLevelError({
                errorCode: result.errorCode.toString(),
                title: result.title,
                message: result.message,
              });
            } else {
              router.navigate({
                pathname: '/otp/error',
                params: {
                  screenType: InformationScreenTypeEnum.Error,
                  errorCode: result.errorCode.toString(),
                  message: result.message,
                  title: result.title,
                  flowKey: flowKey,
                },
              });
            }
          } else {
            const isScreenLevelError = inlineErrorCodes.includes(
              result.errorCode.toString(),
            );
            if (isScreenLevelError) {
              setScreenError({
                message: result.message,
              });
              return;
            }
            setOtpError(translatedTextOtpError('tryAgainMessage'));
          }
        } else {
          // Fallback for any other errors
          setScreenError({
            message: translatedTextOtpError('tryAgainMessage'),
          });
        }
      }
    } catch {
      setScreenError({ message: genericError });
    }
  };

  useEffect(() => {
    if (analyticsError !== null) {
      trackVerificationFailed();
    }
  }, [analyticsError, trackVerificationFailed]);

  const handleResendOtp = async () => {
    setResendCodeAttempt(resendCodeAttempt + 1);
    setSnackbarMessage(null);
    setScreenError(null);
    setOtp('');
    setOtpError(null);
    setIsResendLoading(true);
    Keyboard.dismiss();

    try {
      const { errorCode, message, title } = await onResendOtp();

      if (errorCode) {
        if (screenLevelErrorMessageCodes.includes(errorCode.toString())) {
          if (onScreenLevelError) {
            onScreenLevelError({
              errorCode: errorCode.toString(),
              title,
              message,
            });
          } else {
            router.navigate({
              pathname: '/otp/error',
              params: {
                screenType: InformationScreenTypeEnum.Error,
                errorCode: errorCode.toString(),
                message: message,
                title: title,
                flowKey: flowKey,
              },
            });
          }
        }
        setScreenError({ title, message });
        return;
      }

      setSnackbarMessage(newCodeSentText ?? translatedTextOtp('newCodeSent'));
    } catch {
      setScreenError({ message: genericError });
    } finally {
      setIsResendLoading(false);
    }
  };

  let analyticsIdValue: string;
  if (flowKey === OtpFlowKey.LOGIN) {
    analyticsIdValue = 'resendClickedNewCode';
  } else if (flowKey === OtpFlowKey.PROFILE_CHANGE) {
    analyticsIdValue = 'verification-code-requested';
  } else {
    analyticsIdValue = 'resendClicked';
  }

  return (
    <KeyboardAvoidingContainer>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <NavigationBar
              {...props}
              title={screenTitle}
              primaryOnPress={onBack}
            />
          ),
        }}
      />

      {snackbarMessage ? (
        <Snackbar
          text={snackbarMessage}
          onHide={() => setSnackbarMessage(null)}
        />
      ) : null}

      <KeyboardAvoidingContainer.Content>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {screenError || otpError ? (
            <View style={styles.inLineErrorAlertContainer}>
              <InlineAlert
                alertType={InlineAlertType.Error}
                bodyText={screenError?.message || otpError!}
              />
            </View>
          ) : null}
          <TitleLgText style={styles.headingContainer} numberOfLines={0}>
            {headerText ?? translatedTextOtp('common.header')}
          </TitleLgText>
          <BodyBaseText style={styles.subHeadingContainer} numberOfLines={0}>
            {description}
          </BodyBaseText>
          <VXLTextInput
            autoFocus={shouldAutoFocus ?? true}
            textContentType="oneTimeCode"
            value={otp}
            onChangeText={handleOtpChange}
            editable={!isLoading}
            onSubmitEditing={handleVerifyOtp}
            inputMode="numeric"
            keyboardType="numeric"
            maxLength={OTP_CODE_LENGTH}
            autoComplete="one-time-code"
            testID="otp-input"
          />
          <View style={styles.resendButtonContainer}>
            <ResendOtpButton
              onPress={handleResendOtp}
              isLoading={isResendLoading}
              flowKey={flowKey}
              isDisabled={isLoading}
              resendCodeAttempt={resendCodeAttempt}
              initialCountdown={20}
              verificationType={verificationType} // Pass this to ResendOtpButton
              analyticsId={analyticsIdValue}
              isCode={isCode}
              onExpiry={onCodeExpired}
            />
          </View>
          <View style={styles.inLineAlertContainer}>
            <InlineAlert
              alertType={InlineAlertType.Info}
              isImageVisible={true}
              titleText={translatedTextOtp('otpBanner.title')}
              bodyText={translatedTextOtp('otpBanner.description')}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingContainer.Content>

      <KeyboardAvoidingContainer.Actions>
        <Button
          label={submitButtonLabel ?? translatedTextOtp('buttons.submit')}
          isDisabled={
            !otp ||
            isResendLoading ||
            Boolean(otp && !otpSchema.safeParse(otp).success) ||
            isLoading
          }
          isLoading={isLoading || isResendLoading}
          onPress={handleVerifyOtp}
        />
        {secondaryButtonOnPress ? (
          <Button
            label={
              secondaryButtonLabel ?? translatedTextOtp('buttons.secondary')
            }
            type="SECONDARY"
            onPress={secondaryButtonOnPress}
            analyticsId={secondaryButtonAnalyticsId}
            customAttributes={
              flowKey === OtpFlowKey.REACTIVATION
                ? {
                    reactivation_flow: 'true',
                  }
                : undefined
            }
          />
        ) : null}
      </KeyboardAvoidingContainer.Actions>
    </KeyboardAvoidingContainer>
  );
}

const styles = StyleSheet.create({
  headingContainer: {
    marginBottom: theme.spacing.m,
  },
  inLineAlertContainer: {
    paddingVertical: theme.spacing.m,
  },
  inLineErrorAlertContainer: {
    paddingBottom: theme.spacing.m,
  },
  resendButtonContainer: {
    paddingTop: theme.spacing.m,
  },
  scrollContent: {
    paddingBottom: theme.spacing.xl,
  },
  subHeadingContainer: {
    marginBottom: theme.spacing.m,
  },
});

export default OtpScreen;
